import { useRef, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'
import Box from '@mui/material/Box'
import {
  signupVerifyOtpCellSx,
  signupVerifyOtpDigitInputSx,
  signupVerifyOtpRowSx,
} from '../styles/signupVerifyStyles'
import {
  displayOtpCell,
  mergeOtpDigitAt,
  otpFromClipboard,
  stripNonDigits,
  stripOtpWhitespace,
} from './otpInputHelpers'

const OTP_LENGTH = 6

interface SignupOtpInputProps {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly disabled?: boolean
  readonly error?: boolean
  readonly id?: string
  readonly cellSx?: object | null
}

/** Figma 5074:12890–12895 (cells) on 5074:12889 row — empty 5074:11964 / filled 5074:12873 */
export function SignupOtpInput({
  value,
  onChange,
  disabled = false,
  error = false,
  id = 'signup-otp-code',
  cellSx = null,
}: SignupOtpInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(OTP_LENGTH, ' ').slice(0, OTP_LENGTH).split('')

  const setDigitAt = (index: number, char: string) => {
    const next = mergeOtpDigitAt(digits, index, char)
    onChange(stripOtpWhitespace(next.join('')).slice(0, OTP_LENGTH))
  }

  const handleChange =
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = stripNonDigits(e.target.value)
      if (!raw) {
        setDigitAt(index, '')
        return
      }
      const chars = raw.slice(0, OTP_LENGTH - index).split('')
      let combined = stripOtpWhitespace(digits.join(''))
      chars.forEach((c, offset) => {
        const pos = index + offset
        if (pos < OTP_LENGTH) {
          combined =
            combined.slice(0, pos) + c + combined.slice(pos + 1)
        }
      })
      onChange(combined.slice(0, OTP_LENGTH))
      const nextFocus = Math.min(index + chars.length, OTP_LENGTH - 1)
      inputsRef.current[nextFocus]?.focus()
    }

  const handleKeyDown =
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index]?.trim()) {
        if (index > 0) {
          inputsRef.current[index - 1]?.focus()
        }
      }
    }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const pasted = otpFromClipboard(e.clipboardData.getData('text'), OTP_LENGTH)
    if (!pasted) return
    e.preventDefault()
    onChange(pasted)
    const focusIndex = Math.min(pasted.length, OTP_LENGTH) - 1
    inputsRef.current[focusIndex]?.focus()
  }

  return (
    <Box
      role="group"
      aria-label="6-digit verification code"
      data-testid="signup-otp-input"
      sx={signupVerifyOtpRowSx}
      onPaste={handlePaste}
    >
      {Array.from({ length: OTP_LENGTH }, (_, index) => (
        <Box
          key={index}
          data-testid="signup-verify-otp-cell"
          data-error={error ? 'true' : 'false'}
          data-disabled={disabled ? 'true' : 'false'}
          sx={[signupVerifyOtpCellSx, cellSx]}
        >
          <Box
            component="input"
            id={index === 0 ? id : undefined}
            ref={(el: HTMLInputElement | null) => {
              inputsRef.current[index] = el
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={OTP_LENGTH}
            value={displayOtpCell(digits[index])}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            disabled={disabled}
            aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
            sx={signupVerifyOtpDigitInputSx}
          />
        </Box>
      ))}
    </Box>
  )
}
